import { CAST_AI } from '@cast/constants';
import {
  Alert,
  Button,
  ConditionalWrapper,
  DrawerProvider,
  Icons,
  Tooltip,
  useDrawer,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { WoopAgentCommand } from 'features/cluster/workload-optimization/_components/WoopAgentCommand';
import { usePermitHpaList } from 'features/cluster/workload-optimization/_hooks/permissions/usePermitHpaList';
import { useVersionsInfo } from 'features/cluster/workload-optimization/_hooks/useVersionsInfo';
import { useClusterHpasQuery } from 'features/cluster/workload-optimization/hpa-list/_hooks/useClusterHpas';
import { makeHpaListLink } from 'features/cluster/workload-optimization/navigation';
import { useNavigate } from '@cast/infrastructure-organization';

import { HpaConverterHowItWorksDrawer } from './_components/HpaConverterHowItWorksDrawer';
import { PolicyCardForm } from './_components/PolicyCardForm';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { RffCheckbox } from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

const HpaConverterFieldInner = ({ policyName }: { policyName: string }) => {
  const { open } = useDrawer();
  const { isHpaConverterCannotBeUsed, latestVersion } = useVersionsInfo();
  const navigate = useNavigate();
  const { cluster } = useCluster();
  const { rows } = useClusterHpasQuery({ clusterId: cluster.id });
  const affectedHpaCount = rows.filter(
    (r) => r.scalingPolicyName === policyName
  ).length;

  const openHowItWorks = () => {
    open({ component: HpaConverterHowItWorksDrawer, props: {} });
  };

  const openConversionPreview = () => {
    navigate(makeHpaListLink({ policyName }));
  };

  const { canViewHpaList } = usePermitHpaList();
  return (
    <Stack gap={16} data-testid="hpa-converter-field">
      <PolicyCardForm
        label="Enable HPA converter"
        description={`${CAST_AI} will convert percentage-based utilization targets to average value targets for matching HPAs in this policy.`}
        control={
          <ConditionalWrapper
            condition={isHpaConverterCannotBeUsed}
            wrapper={(child) => (
              <Tooltip
                title={
                  <Stack
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span>
                      Update required:&nbsp;
                      <ExternalLink
                        link={getDocsLink('docs_woop_upgrading')}
                        noIcon
                      >
                        <Typography
                          variant="P12B"
                          color={tokens.theme.fg.selection.primary}
                        >
                          workload-autoscaler {latestVersion}
                        </Typography>
                      </ExternalLink>
                    </span>
                    <WoopAgentCommand />
                  </Stack>
                }
              >
                {child}
              </Tooltip>
            )}
          >
            <RffCheckbox
              name="enabled"
              size="small"
              testId="hpa-converter-input"
              disabled={isHpaConverterCannotBeUsed}
            />
          </ConditionalWrapper>
        }
      />
      <Alert
        size="sm"
        color="info"
        variant="outlined"
        icon={<Icons.Info size={16} />}
        sx={{
          color: tokens.theme.fg.status.info,
          borderColor: tokens.theme.border.status.info,
          backgroundColor: tokens.theme.bg.structural.surface,
          '& .MuiAlert-icon svg': {
            color: tokens.theme.fg.status.info,
          },
          '& .MuiAlert-message': {
            color: tokens.theme.fg.status.info,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={8}
        >
          <Typography variant="P12R" sx={{ color: 'inherit' }}>
            {CAST_AI} doesn&apos;t take ownership of your HPAs and reverts to
            your original spec if disabled.
          </Typography>
          <Typography
            variant="P12M"
            sx={{
              color: tokens.theme.fg.status.info,
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={openHowItWorks}
          >
            Details
          </Typography>
        </Stack>
      </Alert>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          border: '1px dashed',
          borderColor: tokens.theme.border.structural.neutralStrong,
          borderRadius: 1,
          px: 12,
          py: 12,
        }}
      >
        <Stack direction="row" alignItems="center" gap={10}>
          <Icons.Table size={16} />
          <Typography variant="P12M">
            {affectedHpaCount} HPAs will be converted across this policy
          </Typography>
        </Stack>

        {canViewHpaList && (
          <Button
            variant="ghost"
            size="sm"
            endIcon={<Icons.ArrowRight />}
            onClick={openConversionPreview}
          >
            Open conversion preview
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export const HpaConverterField = ({ policyName }: { policyName: string }) => (
  <DrawerProvider>
    <HpaConverterFieldInner policyName={policyName} />
  </DrawerProvider>
);
